/**
 * Phase 7E-A — Browser offline store (IndexedDB). ADDITIVE; does not touch online flows.
 * Object stores: prepared (cached walk working-set), captures (pending metadata + Blob binary + state).
 * "Saved on Device" is only true after a confirmed IndexedDB put resolves. Binaries live in IndexedDB
 * Blob here. In the native app (Expo WebView wrapper) this SAME store runs inside the device WebView's
 * IndexedDB (OS-filesystem-backed, durable across restarts) — build-validated, device NOT certified.
 * NOTE: there is no separate expo-file-system native binary path; storage is IndexedDB in web and native.
 */
const DB_NAME = 'rvx_offline';
const VERSION = 1;

function open() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('prepared')) db.createObjectStore('prepared', { keyPath: 'walkId' });
      if (!db.objectStoreNames.contains('captures')) {
        const s = db.createObjectStore('captures', { keyPath: 'clientKey' });
        s.createIndex('walkId', 'walkId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(store, mode, fn) {
  const db = await open();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const os = t.objectStore(store);
    let out;
    Promise.resolve(fn(os)).then((v) => { out = v; }).catch(reject);
    t.oncomplete = () => resolve(out);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error || new Error('tx aborted'));
  });
}

const req2p = (r) => new Promise((res, rej) => { r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });

export async function isSupported() {
  try { await open(); return true; } catch { return false; }
}

export async function preparedWalk(walkId) {
  return tx('prepared', 'readonly', (os) => req2p(os.get(walkId)));
}

export async function prepareOffline(pack) {
  // pack = {walkId, walk, captureLocations, drawing, revision, preparedAt}
  await tx('prepared', 'readwrite', (os) => req2p(os.put({ ...pack, preparedAt: new Date().toISOString() })));
  const check = await preparedWalk(pack.walkId);
  if (!check) throw new Error('Offline preparation failed to persist');
  return check;
}

/** Persist a capture. Resolves ONLY after IndexedDB confirms the write (durable "Saved on Device"). */
export async function saveCapture(cap) {
  // cap = {clientKey, walkId, entity, tempId, parentTempId?, data, blob?, filename?, mime?, size?, drawingRevision?}
  const rec = { ...cap, state: 'SAVED_ON_DEVICE', createdAt: new Date().toISOString(), serverId: null, error: null };
  try {
    await tx('captures', 'readwrite', (os) => req2p(os.put(rec)));
  } catch (e) {
    throw new Error(`STORAGE_FAILED: ${e && e.name ? e.name : e}`); // quota/persistence failure -> caller must NOT show Saved
  }
  const verify = await tx('captures', 'readonly', (os) => req2p(os.get(cap.clientKey)));
  if (!verify) throw new Error('STORAGE_FAILED: capture not durably persisted');
  return verify;
}

export async function listCaptures(walkId) {
  return tx('captures', 'readonly', (os) => req2p(os.index('walkId').getAll(walkId)));
}

export async function setState(clientKey, patch) {
  return tx('captures', 'readwrite', async (os) => {
    const cur = await req2p(os.get(clientKey));
    if (!cur) return null;
    const next = { ...cur, ...patch };
    await req2p(os.put(next));
    return next;
  });
}

/** Delete local binary+record ONLY after confirmed server persistence (safe cleanup boundary). */
export async function cleanupSynced(clientKey) {
  return tx('captures', 'readwrite', (os) => req2p(os.delete(clientKey)));
}

export async function storageEstimate() {
  if (navigator.storage && navigator.storage.estimate) {
    const e = await navigator.storage.estimate();
    return { usage: e.usage, quota: e.quota };
  }
  return null;
}
